import type { AdminTicket } from "@sd/types/admin";

export type TicketActivityEntry = {
  id: string;
  activity_title: string;
  activity_description: string;
  activity_type: string;
  created_on: string;
  created_by: string;
};

function extractTicketId(title: string): string | null {
  const m = title.match(/\[?(TXT-\d+)\]?/i);
  return m ? m[1] : null;
}

function extractActivityArray(raw: unknown): Record<string, unknown>[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.filter(
      (item): item is Record<string, unknown> =>
        Boolean(item) && typeof item === "object" && !Array.isArray(item)
    );
  }
  if (typeof raw !== "object") return [];

  const root = raw as Record<string, unknown>;
  for (const key of [
    "ticket_history",
    "history",
    "activity_logs",
    "results",
    "data",
    "items",
  ] as const) {
    const value = root[key];
    if (Array.isArray(value)) {
      return value.filter(
        (item): item is Record<string, unknown> =>
          Boolean(item) && typeof item === "object" && !Array.isArray(item)
      );
    }
  }
  return [];
}

function mapHistoryRowToEntry(r: Record<string, unknown>): TicketActivityEntry {
  const apiText = String(r.text ?? "").trim();
  const field = String(r.field ?? r.property ?? r.attribute ?? "").trim();
  const oldVal = String(
    r.old_value ?? r.previous_value ?? r.from_value ?? r.old ?? ""
  ).trim();
  const newVal = String(
    r.new_value ?? r.current_value ?? r.to_value ?? r.new ?? ""
  ).trim();

  let activity_title = String(
    r.activity_title ?? r.title ?? r.action ?? r.event ?? r.change_type ?? ""
  ).trim();
  let activity_description = String(
    r.activity_description ??
      r.description ??
      r.details ??
      r.message ??
      r.comment ??
      ""
  ).trim();

  if (!activity_title && apiText) {
    activity_title = apiText;
  }
  if (!activity_description && apiText) {
    activity_description = apiText;
  }

  if (!activity_title && field) {
    activity_title = `${field} updated`;
  }
  if (!activity_description && field && (oldVal || newVal)) {
    if (field.toLowerCase() === "status" && oldVal && newVal) {
      activity_description = `Previous status: ${oldVal}\nNew status: ${newVal}`;
    } else if (oldVal && newVal) {
      activity_description = `Previous ${field}: ${oldVal}\nNew ${field}: ${newVal}`;
    } else if (newVal) {
      activity_description = `New ${field}: ${newVal}`;
    } else if (oldVal) {
      activity_description = `Previous ${field}: ${oldVal}`;
    }
  }

  return {
    id: String(
      r.id ??
        r.history_id ??
        `${activity_title}-${r.time ?? r.created_on ?? r.changed_at ?? ""}`
    ),
    activity_title,
    activity_description,
    activity_type: String(
      r.activity_type ?? r.type ?? r.event_type ?? field ?? ""
    ),
    created_on: String(
      r.time ??
        r.created_on ??
        r.created_at ??
        r.changed_at ??
        r.changed_on ??
        r.performed_at ??
        r.timestamp ??
        ""
    ),
    created_by: String(
      r.by ??
        r.by_name ??
        r.created_by_name ??
        r.performed_by_name ??
        r.user_name ??
        r.changed_by_name ??
        r.created_by ??
        r.performed_by ??
        r.changed_by ??
        r.user ??
        ""
    ),
  };
}

export function parseTicketActivityEntries(raw: unknown): TicketActivityEntry[] {
  return extractActivityArray(raw).map(mapHistoryRowToEntry);
}

export function activityEntryMatchesTicket(
  entry: TicketActivityEntry,
  ticket: AdminTicket
): boolean {
  const uiId = ticket.uiId?.trim();
  const id = ticket.id.trim();
  const title = entry.activity_title;
  const desc = entry.activity_description;

  if (uiId) {
    if (title.includes(uiId) || desc.includes(uiId)) return true;
    const extracted = extractTicketId(title);
    if (extracted && extracted.toUpperCase() === uiId.toUpperCase()) {
      return true;
    }
  }
  if (id && (title.includes(id) || desc.includes(id))) return true;
  return false;
}

export function extractStatusChangeFromActivity(
  desc: string
): { prev: string; next: string } | null {
  const prev = desc.match(/Previous status:\s*(\S+)/i)?.[1];
  const next = desc.match(/New status:\s*(\S+)/i)?.[1];
  if (prev && next) return { prev, next };
  return null;
}

/** Human-readable bullet lines for history (status, assignee, etc.). */
export function extractActivityChangeBullets(desc: string): string[] {
  const bullets: string[] = [];
  const trimmed = desc.trim();
  if (!trimmed) return [];

  if (/^created by\b/i.test(trimmed)) {
    return [];
  }

  const statusFromTo = trimmed.match(
    /status changed from\s+(.+?)\s+to\s+(.+?)$/i
  );
  if (statusFromTo) {
    bullets.push(
      `Status changed from ${statusFromTo[1].trim()} to ${statusFromTo[2].trim()}`
    );
    return bullets;
  }

  const assignFromTo = trimmed.match(
    /assigned to changed from\s+(.+?)\s+to\s+(.+?)$/i
  );
  if (assignFromTo) {
    bullets.push(
      `Assignee changed from ${assignFromTo[1].trim()} to ${assignFromTo[2].trim()}`
    );
    return bullets;
  }

  const statusChange = extractStatusChangeFromActivity(desc);
  if (statusChange) {
    bullets.push(
      `Status changed from ${statusChange.prev} to ${statusChange.next}`
    );
  }

  const assignPrev = desc.match(/Previous assignee:\s*(.+)/i)?.[1]?.trim();
  const assignNext = desc.match(/New assignee:\s*(.+)/i)?.[1]?.trim();
  if (assignPrev && assignNext) {
    bullets.push(`Assignee changed from ${assignPrev} to ${assignNext}`);
  }

  const priorityPrev = desc.match(/Previous priority:\s*(.+)/i)?.[1]?.trim();
  const priorityNext = desc.match(/New priority:\s*(.+)/i)?.[1]?.trim();
  if (priorityPrev && priorityNext) {
    bullets.push(`Priority changed from ${priorityPrev} to ${priorityNext}`);
  }

  if (bullets.length > 0) {
    return bullets;
  }

  const lines = trimmed
    .split(/\n+/)
    .map(l => l.trim())
    .filter(Boolean);
  if (lines.length <= 3) {
    return lines;
  }
  return [lines[0]];
}

export function sortActivityEntriesChronological(
  entries: TicketActivityEntry[]
): TicketActivityEntry[] {
  return [...entries].sort((a, b) => {
    const ta = new Date(a.created_on).getTime();
    const tb = new Date(b.created_on).getTime();
    if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
    if (Number.isNaN(ta)) return 1;
    if (Number.isNaN(tb)) return -1;
    return ta - tb;
  });
}

export function isPropertyChangeEntry(entry: TicketActivityEntry): boolean {
  const blob = `${entry.activity_title} ${entry.activity_description}`.toLowerCase();
  return (
    /previous (status|assignee|priority)/i.test(entry.activity_description) ||
    /status changed|assignee changed|priority changed|assigned to changed/i.test(
      blob
    ) ||
    /status updated|assigned to|unassigned|priority/i.test(blob) ||
    /status_update|assignee_update|priority_update/i.test(
      entry.activity_type.toLowerCase()
    )
  );
}

export function formatTimeAgo(dateStr: string): string {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  if (Number.isNaN(diff)) return "";
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
}
